import { Info, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipButton } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { quoteClip } from "data/mutations-common";
import {
  fetchClipSummary,
  fetchCredits,
  fetchSubscription,
} from "data/queries-common";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory } from "react-router";
import { useIntercom } from "react-use-intercom";
import * as Routes from "routes";
import { toTitleCase } from "utils/commons";
import { openDownloadLinkCallback } from "utils/download";
import { resolveIdentityId } from "utils/identity";
import {
  ClipConfigProps,
  ClipStepsControlProps,
  hasPremiumData,
} from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import {
  QUOTE_VALID_SECONDS,
  formatCRS,
  formatFileFormat,
  hasLiveQuote,
} from "../../../clip-utils";
import { PurchaseWithCredits } from "./purchase-with-credits";
import { PurchaseWithDollars } from "./purchase-with-dollars";
import { SummaryTable } from "./summary-table";

export const ClipSummary = (props: ClipConfigProps & ClipStepsControlProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const history = useHistory();

  const { enqueueQueryFailed, enqueueMutationFailed } = useCustomSnackbars();
  const { isPermitted, permissionDeniedMessageString } = usePermissions();

  // Check if clip has premium data to determine which permission to check
  const clipHasPremiumDataForDownload = hasPremiumData(props.clipConfig);

  const clipSummaryQuery = useQuery({
    queryKey: [QueryKeys.clipSummaryKey, identityId],
    queryFn: () => fetchClipSummary(props.clipConfig.id),
  });

  useEffect(() => {
    if (clipSummaryQuery.isError) {
      enqueueQueryFailed(clipSummaryQuery.error.toString());
    }
  }, [clipSummaryQuery.isError]);

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  useEffect(() => {
    if (subscriptionQuery.isError) {
      enqueueQueryFailed(subscriptionQuery.error.toString());
    }
  }, [subscriptionQuery.isError]);

  // PRELOAD CREDITS QUERY
  const creditsQuery = useQuery({
    queryKey: [QueryKeys.credits, identityId],
    queryFn: () => fetchCredits(),
  });

  const [subStep, setSubStep] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const isSummaryOnly = props.clipConfig.status !== "Quoted";
  const timeDelta = dayjs().utc().unix() - (props.clipConfig.quotedOn ?? 0);
  const isExpired = timeDelta > QUOTE_VALID_SECONDS;
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Stay on the summary while the requote is in flight. Rewinding to the
  // Finalise step here (as this used to) rendered an enabled "Quote" button over
  // an already-submitted quote, so a second click double-submitted the job.
  const onQuoteSubmit = () => {
    clipsQuoteMutation.mutate(props.clipConfig.id);
  };

  const clipsQuoteMutation = useMutation({
    mutationFn: (clipId: string) => quoteClip(clipId),
    onSuccess: (clipConfig: ClipConfiguration) => {
      props.setClipConfig((prev: ClipConfiguration) => {
        return {
          ...prev,
          status: clipConfig.status,
        };
      });

      (queryClient.invalidateQueries({
        queryKey: [QueryKeys.clipsKey, identityId],
      }),
        enqueueSnackbar("Clip has been submitted for quoting.", {
          variant: "success",
        }));
      props.resetClipConfig();
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.toString());
    },
  });

  const onEdit = () => {
    const newClip: ClipConfiguration = {
      ...props.clipConfig,
      activeStep: 1,
      status: "Draft",
    };

    props.setClipConfig(newClip);
    props.saveClipConfig(newClip);
  };
  const { show } = useIntercom();

  
  const isSold =
    props.clipConfig.status === "Processing" ||
    props.clipConfig.status === "Completed";
  const hideStaleFigures = !isSold && !hasLiveQuote(props.clipConfig);

  const clipTooBig =
    clipSummaryQuery.isSuccess &&
    subscriptionQuery.isSuccess &&
    subscriptionQuery.data.activeSubscription &&
    clipSummaryQuery.data.dollarCost > 10000;

  const isCashPaymentDisabled = !isPermitted([PermissionsKeys.clipPayDollars]);
  const canPurchaseOversizedClip = isPermitted([PermissionsKeys.clipOversized]);

  // Check if clip contains premium data and if user has permission to submit it
  const clipHasPremiumData = hasPremiumData(props.clipConfig);
  const hasPremiumDataPermission = isPermitted([
    PermissionsKeys.premiumDataCreate,
  ]);
  const isPremiumDataBlocked = clipHasPremiumData && !hasPremiumDataPermission;
  const premiumDataTooltip = isPremiumDataBlocked
    ? permissionDeniedMessageString([PermissionsKeys.premiumDataCreate])
    : "";

  return (
    <>
      {subStep == 0 && (
        <>
          <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-auto *:shrink-0">
            {/* Not an order until it has been paid for, and a requote in flight
                is still a quote. */}
            <h5 className="text-lg font-medium">{`${
              props.clipConfig.status === "Quoted" ||
              props.clipConfig.status === "Quoting"
                ? "Quote"
                : "Order"
            } Summary`}</h5>
            {/* Says why the figures are missing: `hideStaleFigures` blanks them
                until Platform's new counts land. */}
            {props.clipConfig.status === "Quoting" && (
              <Alert>
                <Loader2 className="size-4 animate-spin" />
                <AlertDescription>
                  Quoting your clip, you will be notified when it's ready
                </AlertDescription>
              </Alert>
            )}
            {props.clipConfig.status === "Quoted" && !isExpired && (
              <Alert>
                <Info className="size-4" />
                <AlertDescription>Quote is valid for 5 days</AlertDescription>
              </Alert>
            )}
            {props.clipConfig.status === "Quoted" && isExpired && (
              <Alert variant="destructive">
                <AlertTitle>Quote has expired</AlertTitle>
                <AlertDescription>
                  Please click 'Requote' button to submit this again.
                </AlertDescription>
              </Alert>
            )}
            {props.clipConfig.status === "Processing" && (
              <Alert>
                <Info className="size-4" />
                <AlertDescription>
                  Clip Processing, you will be notified when it's ready
                </AlertDescription>
              </Alert>
            )}
            <div className="min-h-[43px]">
              <Label>Name</Label>
              <p className="text-sm truncate">{props.clipConfig.name}</p>
            </div>
            <div className="min-h-[43px]">
              <Label>Format</Label>
              <p className="text-sm">
                {formatFileFormat(props.clipConfig.format)}
              </p>
            </div>
            <div className="min-h-[43px]">
              <Label>Coordinate Reference System</Label>
              <p className="text-sm">{formatCRS(props.clipConfig.crs)}</p>
            </div>
            {props.clipConfig.clientReference && (
              <div className="min-h-[43px]">
                <Label>Client Reference</Label>
                <p className="text-sm wrap-break-word">{props.clipConfig.clientReference}</p>
              </div>
            )}
            {props.clipConfig.notes && (
              <div className="min-h-[43px]">
                <Label>Notes</Label>
                <p className="text-sm wrap-break-word">{props.clipConfig.notes}</p>
              </div>
            )}
            <p className="text-sm font-medium">Data</p>

            {clipSummaryQuery.isSuccess && subscriptionQuery.isSuccess && (
              <>
                <SummaryTable
                  quoteSummary={clipSummaryQuery.data}
                  showCredits={
                    !hideStaleFigures &&
                    !(clipTooBig && !canPurchaseOversizedClip)
                  }
                  showFeatures={!hideStaleFigures}
                  showRate={
                    !hideStaleFigures &&
                    !(clipTooBig && !canPurchaseOversizedClip)
                  }
                  showTotal={!hideStaleFigures}
                />
                {!isExpired &&
                  clipSummaryQuery.data.totalFeatures > 0 &&
                  (!clipTooBig || canPurchaseOversizedClip) && (
                    <>
                      <h5 className="text-lg font-medium">Quote</h5>
                      <p className="text-sm font-bold">
                        {`Credits due: ${clipSummaryQuery.data.totalCredits.toLocaleString()}`}
                      </p>
                      {!isCashPaymentDisabled &&
                        subscriptionQuery.data.activeSubscription && (
                          <p className="text-sm font-bold">
                            {`Dollar due: $${clipSummaryQuery.data.dollarCost.toLocaleString(
                              "en-AU",
                              {
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                              },
                            )} inc GST`}
                          </p>
                        )}
                    </>
                  )}
                {clipSummaryQuery.data.totalFeatures === 0 && (
                  <Alert>
                    <AlertDescription>
                      The quoted area contains no features. Return to edit mode to
                      modify the clipped area.
                    </AlertDescription>
                  </Alert>
                )}
                {clipTooBig && !canPurchaseOversizedClip && (
                  <Alert>
                    <Info className="size-4" />
                    <AlertTitle>Quote too large</AlertTitle>
                    <AlertDescription>
                      Talk to us about a commercial license, it is significantly
                      better value.
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2"
                        onClick={() => show()}
                      >
                        Contact
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                {isPremiumDataBlocked && (
                  <Alert>
                    <AlertTitle>Upgrade required to purchase</AlertTitle>
                    <AlertDescription>
                      This clip includes premium data. To complete your purchase,
                      please upgrade to the Team plan.
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-2"
                        onClick={() =>
                          history.push(Routes.orgManageSubscription)
                        }
                      >
                        Upgrade
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
            {!isSummaryOnly &&
              (clipSummaryQuery.isPending || subscriptionQuery.isPending) && (
                <Skeleton className="h-[100px] w-full" />
              )}
            {((!isSummaryOnly && clipSummaryQuery.isError) ||
              subscriptionQuery.isError) && (
              <Alert variant="destructive">
                <AlertDescription>Unable to fetch quote summary</AlertDescription>
              </Alert>
            )}
          </div>
          <div className="self-end flex pt-2 gap-2">
            {!isSummaryOnly &&
              !isExpired &&
              clipSummaryQuery.isSuccess &&
              subscriptionQuery.isSuccess && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setEditModalOpen(true)}
                  >
                    Edit
                  </Button>
                  <ConfirmationModal
                    isLoading={false}
                    isError={false}
                    open={editModalOpen}
                    header="Confirm Clip Modification"
                    body="Editing this clip will cancel the current quote. A new quoting process will begin once you resubmit the clip"
                    leftButtonText={"Cancel"}
                    leftButtonColor="primary"
                    handleLeftButton={() => setEditModalOpen(false)}
                    rightButtonText={"Edit Clip"}
                    rightButtonColor="warning"
                    handleRightButton={onEdit}
                    handleClose={() => setEditModalOpen(false)}
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          onClick={() => {
                            setSubStep(2);
                          }}
                          disabled={
                            subscriptionQuery.isPending ||
                            isCashPaymentDisabled ||
                            clipSummaryQuery.isPending ||
                            clipSummaryQuery?.data?.totalFeatures === 0 ||
                            clipTooBig ||
                            !subscriptionQuery.data.activeSubscription ||
                            isPremiumDataBlocked
                          }
                        >
                          Pay $
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {premiumDataTooltip && (
                      <TooltipContent>{premiumDataTooltip}</TooltipContent>
                    )}
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          disabled={
                            clipSummaryQuery.isPending ||
                            clipSummaryQuery?.data?.totalFeatures === 0 ||
                            (!canPurchaseOversizedClip && clipTooBig) ||
                            !subscriptionQuery.data.activeSubscription ||
                            isPremiumDataBlocked
                          }
                          onClick={() => setSubStep(1)}
                        >
                          Pay Credits
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {premiumDataTooltip && (
                      <TooltipContent>{premiumDataTooltip}</TooltipContent>
                    )}
                  </Tooltip>
                </>
              )}
            {props.clipConfig.status === "Quoted" && isExpired && (
              <Button
                disabled={clipsQuoteMutation.isPending}
                onClick={async () => {
                  await onQuoteSubmit();
                }}
              >
                {clipsQuoteMutation.isPending && (
                  <Loader2 className="size-4 mr-2 animate-spin" />
                )}
                Requote
              </Button>
            )}
          </div>
        </>
      )}
      {subStep == 1 &&
        clipSummaryQuery.isSuccess &&
        subscriptionQuery.isSuccess &&
        subscriptionQuery.data.activeSubscription && (
          <PurchaseWithCredits
            setClipConfig={props.setClipConfig}
            resetClipConfig={props.resetClipConfig}
            clipConfig={props.clipConfig}
            quoteSummary={clipSummaryQuery.data}
            planName={toTitleCase(
              subscriptionQuery.data.activeSubscription.plan
                .product_display_name,
            )}
            setSubStep={setSubStep}
          />
        )}
      {subStep == 2 &&
        clipSummaryQuery.isSuccess &&
        subscriptionQuery.isSuccess &&
        subscriptionQuery.data.activeSubscription && (
          <PurchaseWithDollars
            clipConfig={props.clipConfig}
            quoteSummary={clipSummaryQuery.data}
            plan={subscriptionQuery.data.activeSubscription.plan}
            resetClipConfig={props.resetClipConfig}
            setSubStep={setSubStep}
          />
        )}
      {(props.clipConfig.status === "Processing" ||
        props.clipConfig.status === "Completed") && (
        <div className="self-end mt-auto pt-2 flex">
          <Button
            variant="ghost"
            disabled={!props.clipConfig.invoiceUrl}
            onClick={() => {
              window.open(props.clipConfig.invoiceUrl, "_blank");
            }}
          >
            Download Invoice
          </Button>
          {clipHasPremiumDataForDownload ? (
            <PermissionTooltipButton
              permissionKeys={[PermissionsKeys.premiumDataRead]}
              additionalDisabled={props.clipConfig.status !== "Completed"}
              onClick={openDownloadLinkCallback(
                `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                  props.clipConfig.id
                }/output`,
              )}
            >
              Download
            </PermissionTooltipButton>
          ) : (
            <Button
              variant="ghost"
              disabled={props.clipConfig.status !== "Completed"}
              onClick={openDownloadLinkCallback(
                `${import.meta.env.VITE_DELIVERY_API_URL}/clip/${
                  props.clipConfig.id
                }/output`,
              )}
            >
              Download
            </Button>
          )}
        </div>
      )}
    </>
  );
};
